import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import TopBar from "@/components/TopBar";
import AssetCard, { AssetRow } from "@/components/AssetCard";
import AssetDetailModal from "@/components/AssetDetailModal";
import api from "@/lib/api";
import { Search as SearchIcon, Folder, X, Globe, ArrowLeft } from "lucide-react";

export default function Search() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") || "";
  const folderParam = params.get("folder") || "";

  const [search, setSearch] = useState(q);
  const [folders, setFolders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("wd_view") || "grid");
  const [active, setActive] = useState(null);

  const scopeFolder = useMemo(
    () => folders.find((f) => f.id === folderParam) || null,
    [folders, folderParam]
  );

  useEffect(() => { setSearch(q); }, [q]);
  useEffect(() => { api.get("/folders").then((r) => setFolders(r.data)); }, []);

  useEffect(() => {
    if (!q) { setAssets([]); return; }
    const params = { q, recursive: true };
    if (folderParam) params.folder_id = folderParam;
    api.get("/assets", { params }).then((r) => setAssets(r.data));
  }, [q, folderParam]);

  const clearFolder = () => navigate(`/search?q=${encodeURIComponent(q)}`);
  const applyFolder = (id) => navigate(`/search?q=${encodeURIComponent(q)}&folder=${id}`);
  // Universal "exit search" — return the user to the browse view they came
  // from. If a folder scope is active, land back in that folder. Otherwise
  // drop them at the root dashboard. Used by the folder-pill X, the top-bar
  // clear-search X, and the prominent "← Exit search" button.
  const exitToOrigin = () => navigate(folderParam ? `/folders/${folderParam}` : "/");

  return (
    <div className="flex min-h-screen grain-overlay">
      <Sidebar folders={folders} currentFolderId={folderParam || null} onRefresh={() => api.get("/folders").then((r) => setFolders(r.data))} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          view={view}
          setView={setView}
          onSearch={setSearch}
          searchValue={search}
          onUpload={() => {}}
          canUpload={false}
          scope={scopeFolder ? { folderId: scopeFolder.id, folderName: scopeFolder.name } : null}
        />
        <main className="flex-1 px-4 sm:px-6 md:px-10 lg:px-12 py-6 md:py-8 pb-20">
          <div className="mb-6 flex items-start gap-3">
            <SearchIcon className="w-5 h-5 text-[var(--brand-accent)] mt-1.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Search results for</div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight">&ldquo;{q}&rdquo;</h1>
              <div className="text-sm text-zinc-500 mt-1">{assets.length} {assets.length === 1 ? "match" : "matches"}</div>
              {assets.length > 20 && !/^".+"$/.test(q.trim()) && q.trim().includes(" ") && (
                <div
                  data-testid="phrase-tip-banner"
                  className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--brand-accent)]/80 bg-[var(--brand-accent)]/5 border border-[var(--brand-accent)]/20 px-2 py-1 rounded-full"
                >
                  Tip: wrap in <code className="normal-case tracking-normal font-mono">&ldquo;quotes&rdquo;</code> for an exact-phrase match
                </div>
              )}
            </div>
            <button
              data-testid="exit-search-btn"
              onClick={exitToOrigin}
              className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/50 hover:bg-[var(--brand-accent)]/20 hover:border-[var(--brand-accent)] px-3 py-2 rounded-full transition"
              title={folderParam ? "Return to the folder you searched from" : "Return to the dashboard"}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Exit search
            </button>
          </div>

          {/* Scope toggle */}
          <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="search-scope-bar">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Scope</span>
            <button
              data-testid="scope-all-btn"
              onClick={clearFolder}
              className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border transition ${!folderParam ? "border-[var(--brand-accent)]/60 text-[var(--brand-accent)] bg-[var(--brand-accent)]/10" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"}`}
            >
              <Globe className="w-3 h-3" /> All videos
            </button>
            {scopeFolder && (
              <button
                data-testid="scope-folder-btn"
                onClick={() => applyFolder(scopeFolder.id)}
                className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border transition ${folderParam ? "border-[var(--brand-accent)]/60 text-[var(--brand-accent)] bg-[var(--brand-accent)]/10" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"}`}
              >
                <Folder className="w-3 h-3" /> {scopeFolder.name}
                {folderParam && (
                  <span
                    data-testid="scope-folder-clear-x"
                    onClick={(e) => { e.stopPropagation(); exitToOrigin(); }}
                    className="ml-1 hover:text-red-400 cursor-pointer"
                    title="Exit search and return to this folder"
                  >
                    <X className="w-3 h-3" />
                  </span>
                )}
              </button>
            )}
            {!scopeFolder && folders.length > 0 && (
              <div className="relative group">
                <button className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-200 transition">
                  <Folder className="w-3 h-3" /> Narrow to folder…
                </button>
                <div className="absolute top-full left-0 mt-1 min-w-[220px] bg-zinc-950 border border-zinc-800 rounded-lg shadow-xl opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition z-30 max-h-80 overflow-y-auto py-1">
                  {folders.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => applyFolder(f.id)}
                      className="w-full text-left flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-900 hover:text-[var(--brand-accent)] transition"
                    >
                      <Folder className="w-3 h-3 opacity-60" /> {f.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {assets.length === 0 ? (
            <div className="py-24 text-center text-zinc-500">
              No results. {folderParam ? "Try switching to All videos, or a different keyword." : "Try a different keyword or tag."}
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {assets.map((a) => <AssetCard key={a.id} asset={a} onClick={() => setActive(a.id)} />)}
            </div>
          ) : (
            <div className="border-t border-zinc-900/60">
              {assets.map((a) => <AssetRow key={a.id} asset={a} onClick={() => setActive(a.id)} />)}
            </div>
          )}
        </main>
      </div>

      {active && (
        <AssetDetailModal
          assetId={active}
          onClose={() => setActive(null)}
          onChange={() => {
            const p = { q, recursive: true };
            if (folderParam) p.folder_id = folderParam;
            api.get("/assets", { params: p }).then((r) => setAssets(r.data));
          }}
        />
      )}
    </div>
  );
}
